import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Trophy, ArrowLeft, Users, Plus, X, Trash2, Zap, ExternalLink, QrCode,
  Play, CheckCircle2, Copy, ListChecks, GitBranch, BarChart3, Crown, Mail, Send, Clock, DollarSign, CircleCheck, CircleX, AlertCircle, Monitor, Download, RefreshCw, RotateCcw,
  Eye, EyeOff, ChevronLeft, ChevronRight, Wallet, Megaphone, ArrowDown, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi, useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import TournamentBracket, { MatchListView, ColumnsView, groupRounds, buildDropMap } from '../components/TournamentBracket';
import { buildLogoMap, groupByDivision, TeamLogo, LogoLightbox, useLogoLightbox } from '../components/TeamLogo';
import StandingsTable from '../components/StandingsTable';
import RankingPanel from '../components/RankingPanel';
import PlayerManagementPanel, { PaymentReconcileModal, balanceOf } from '../components/PlayerManagementPanel';
import EventTabs from '../components/EventTabs';
import { downloadQR } from '../utils/qrDownload';
import { usePublicBase } from '../utils/shortUrl';
import { fmtDate } from '../utils/dates';
import { useBackdropClose } from '../utils/backdropClose';
import ConfirmModal from '../components/ConfirmModal';
import PaymentLinkModal from '../components/PaymentLinkModal';
import OrganizerAccessCard from '../components/OrganizerAccessCard';
import CoordinatorQrPanel from '../components/CoordinatorQrPanel';

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none',
};
const RED = '#E63946';
const STATUS_COLORS = {
  draft: '#737373', registration_open: '#22C55E', registration_closed: '#F59E0B',
  in_progress: '#E63946', completed: '#3B82F6', cancelled: '#525252',
};
const MATCH_STATUS = {
  pending: { c: '#737373', l: 'Pending' }, live: { c: '#E63946', l: 'LIVE' },
  completed: { c: '#22C55E', l: 'Done' }, bye: { c: '#525252', l: 'BYE' }, cancelled: { c: '#525252', l: 'Cancelled' },
};

function computeChampion(format, matches) {
  // See WinnersPodium.computePodium — must not crown the GF1 winner while the
  // double-elim bracket reset is still pending/live.
  const resetM = matches.find(m => m.bracket_type === 'final_reset');
  const gf1 = matches.find(m => m.bracket_type === 'final');
  let fin = null;
  if (resetM && (resetM.status === 'pending' || resetM.status === 'live')) {
    fin = null;
  } else if (resetM && resetM.status === 'completed') {
    fin = resetM;
  } else if (gf1 && gf1.status === 'completed') {
    fin = gf1;
  } else if (format === 'single_elimination') {
    const nonThird = matches.filter(m => m.bracket_type !== 'third_place' && m.bracket_type !== 'losers' && m.stage !== 'pool');
    if (nonThird.length) {
      const maxRound = Math.max(...nonThird.map(m => m.round || 0));
      fin = nonThird.filter(m => (m.round || 0) === maxRound && m.status === 'completed' && m.winner_side)[0] || null;
    }
  }
  if (!fin || !fin.winner_side) return null;
  return fin.winner_side === 'a' ? fin.team_a_name : fin.team_b_name;
}

// Renders one division's bracket (SE / DE). RR/list handled by MatchListView upstream.
function SingleDivisionBracket({ format, matches, slug, logoMap, onLogo, onMatch, onReopen, onShowQR }) {
  const champ = computeChampion(format, matches);
  if (format === 'double_elimination') {
    const wb = matches.filter(m => m.bracket_type === 'winners');
    const lb = matches.filter(m => m.bracket_type === 'losers');
    const finals = matches.filter(m => m.bracket_type === 'final' || (m.bracket_type === 'final_reset' && m.status !== 'cancelled'));
    const dropMap = buildDropMap(matches);
    return (
      <div className="space-y-6">
        <div>
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: '#34D399' }}><GitBranch className="h-3.5 w-3.5" style={{ flex: 'none' }} />Winners Bracket</p>
          <TournamentBracket rounds={groupRounds(wb)} showChampion={false} onMatch={onMatch} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR} dropMap={dropMap} />
        </div>
        {lb.length > 0 && (
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: '#F59E0B' }}><ArrowDown className="h-3.5 w-3.5" style={{ flex: 'none' }} />Losers Bracket</p>
            <ColumnsView matches={lb} onMatch={onMatch} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR} />
          </div>
        )}
        {finals.length > 0 && (
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: '#E63946' }}><Trophy className="h-3.5 w-3.5" style={{ flex: 'none' }} />Grand Final{champ ? ` · Champion: ${champ}` : ''}</p>
            <div className="flex gap-2 items-start rounded-lg mb-3" style={{ background: '#12100A', border: '1px solid #2A2410', padding: '9px 12px', maxWidth: 460 }}>
              <Info className="h-3.5 w-3.5 mt-0.5" style={{ color: '#F59E0B', flex: 'none' }} />
              <p className="text-xs" style={{ color: '#C9B98A', lineHeight: 1.5 }}>
                The <b style={{ color: '#FFD54A', fontWeight: 700 }}>winners-bracket</b> side needs one win; the <b style={{ color: '#FFD54A', fontWeight: 700 }}>losers-bracket</b> side must win twice — taking Grand Final 1 forces a <b style={{ color: '#FFD54A', fontWeight: 700 }}>bracket reset</b> (one deciding match, both teams at 1 loss).
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3" style={{ maxWidth: 460 }}>
              {finals.map(m => (
                <div key={m.id}>
                  <p className="text-[9px] font-bold uppercase mb-1" style={{ letterSpacing: '0.1em', color: m.bracket_type === 'final_reset' ? '#FF6B78' : '#8A93A3' }}>
                    {m.bracket_type === 'final_reset' ? 'Bracket reset · winner-take-all' : 'Grand Final'}
                  </p>
                  <MatchCard m={m} divisions={[]} slug={slug} onReopen={onReopen} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  const bronze = matches.find(m => m.bracket_type === 'third_place');
  return (
    <TournamentBracket rounds={groupRounds(matches.filter(m => m.bracket_type !== 'third_place'))}
      champion={champ} bronzeMatch={bronze || null} onMatch={onMatch} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR} />
  );
}

// Round-robin standings with PF/PA and an expandable per-match score breakdown.
// Compute round-robin standings rows for a set of teams + their completed matches.
function computeRows(teamList, matchList) {
  const stat = {};
  teamList.forEach(t => { stat[t.id] = { team_id: t.id, team_name: t.team_name, played: 0, wins: 0, losses: 0, points_for: 0, points_against: 0, diff: 0 }; });
  matchList.filter(m => m.status === 'completed').forEach(m => {
    let pa = 0, pb = 0;
    (m.games || []).forEach(g => { pa += g.score_a || 0; pb += g.score_b || 0; });
    const a = stat[m.team_a_id], b = stat[m.team_b_id];
    if (a) { a.played++; a.points_for += pa; a.points_against += pb; m.winner_side === 'a' ? a.wins++ : a.losses++; }
    if (b) { b.played++; b.points_for += pb; b.points_against += pa; m.winner_side === 'b' ? b.wins++ : b.losses++; }
  });
  return Object.values(stat).map(r => ({ ...r, diff: r.points_for - r.points_against }))
    .sort((x, y) => (y.wins - x.wins) || (y.diff - x.diff) || (y.points_for - x.points_for));
}

// Pool stage: per-pool standings + matches, grouped by pool.
function PoolStageView({ poolMatches, teams, advancePerPool, logoMap, onMatch, onLogo, onShowQR, onReopen }) {
  const poolIds = [...new Set(poolMatches.map(m => m.pool_id))].sort();
  return (
    <div className="space-y-8">
      {poolIds.map(pid => {
        const pMatches = poolMatches.filter(m => m.pool_id === pid);
        const pName = pMatches[0]?.pool_name || 'Pool';
        const pTeams = teams.filter(tm => tm.pool_id === pid);
        const rows = computeRows(pTeams, pMatches);
        return (
          <div key={pid} data-testid={`pool-${pid}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold uppercase px-3 py-1 rounded-lg" style={{ background: '#E6394618', color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>{pName}</span>
              <span className="text-xs" style={{ color: '#525252' }}>top {advancePerPool} advance</span>
            </div>
            <div className="rounded-xl overflow-hidden mb-3" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
              <StandingsTable rows={rows.map((r, i) => ({ ...r, _adv: i < advancePerPool }))} matches={pMatches} logoMap={logoMap} onLogo={onLogo} />
            </div>
            <MatchListView matches={pMatches} onMatch={onMatch} logoMap={logoMap} onLogo={onLogo} onShowQR={onShowQR} onReopen={onReopen} />
          </div>
        );
      })}
    </div>
  );
}


function ScorerQRModal({ match, onClose, onCalled }) {
  const backdrop = useBackdropClose(onClose);
  const [calling, setCalling] = useState(false);
  if (!match) return null;
  const scorerUrl = `${window.location.origin}/sports/score/${match.qr_token}`;
  const isLive = match.status === 'live';
  // Call on court → the existing scorer/start endpoint sets the match live and
  // takes a free court (none free → it plays floating, shown as "Court TBA").
  const callOnCourt = async () => {
    setCalling(true);
    try {
      await sportsApi.post(`/sports/tournaments/score/${match.qr_token}/start`);
      toast.success('Match called on court');
      onCalled?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the match');
    } finally { setCalling(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }} {...backdrop} data-testid="scorer-qr-modal">
      <div className="rounded-2xl p-5 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', maxWidth: 340, width: '100%' }} onClick={e => e.stopPropagation()}>
        <p className="text-sm font-bold mb-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>Match {match.match_number}{match.court_name ? ` · ${match.court_name}` : (isLive ? ' · Court TBA' : '')}</p>
        <p className="text-xs mb-3" style={{ color: '#737373' }}>{match.team_a_name} vs {match.team_b_name}</p>
        <div className="flex flex-col items-center p-4 rounded-xl" style={{ background: '#fff' }}>
          <QRCodeSVG value={scorerUrl} size={180} level="H" includeMargin />
          <p className="text-xs mt-2 font-semibold" style={{ color: '#0A0A0A' }}>Referee scans to score</p>
        </div>
        <a href={scorerUrl} target="_blank" rel="noreferrer" data-testid="qr-open-scoring"
          className="mt-3 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold"
          style={{ background: '#1E1E1E', border: '1px solid #333', color: '#fff', textDecoration: 'none' }}>
          <ExternalLink className="h-4 w-4" /> Open scoring module
        </a>
        {!isLive && (
          <button data-testid="qr-call-on-court" onClick={callOnCourt} disabled={calling}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-black uppercase tracking-wide"
            style={{ background: 'linear-gradient(135deg, #FF1A3E, #FF6B35)', color: '#fff', border: 'none', boxShadow: '0 0 16px rgba(255,26,62,0.3)', opacity: calling ? 0.6 : 1, fontFamily: 'Oswald, sans-serif' }}>
            <Megaphone className="h-4 w-4" /> {calling ? 'Calling…' : 'Call on court'}
          </button>
        )}
        {!isLive && <p className="text-[11px] mt-1.5" style={{ color: '#555' }}>Takes the next free court · none free → plays floating</p>}
        <button onClick={onClose} className="mt-3 px-5 py-2 rounded-lg text-sm font-bold" style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#A3A3A3' }}>Close</button>
      </div>
    </div>
  );
}

function AddTeamForm({ tournament, slug, onAdded }) {
  const size = tournament.game_mode === 'singles' ? 1 : (tournament.team_size || 2);
  const [names, setNames] = useState(Array(size).fill(''));
  const [teamName, setTeamName] = useState('');
  const [divId, setDivId] = useState('');
  const [logoKey, setLogoKey] = useState(null);
  const [logoThumbKey, setLogoThumbKey] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const setName = (i, v) => setNames(arr => arr.map((x, idx) => idx === i ? v : x));
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const uploadLogo = async (file) => {
    if (!file || !slug) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await import('axios').then(ax => ax.default.post(
        `${API}/sports/public/${slug}/tournaments/${tournament.id}/upload-logo`, fd));
      setLogoKey(r.data.logo_key); setLogoThumbKey(r.data.logo_thumb_key);
      setLogoPreview(URL.createObjectURL(file));
    } catch { toast.error('Logo upload failed'); }
    finally { setLogoUploading(false); }
  };

  const submit = async () => {
    const players = names.map(n => n.trim()).filter(Boolean).map(n => ({ name: n }));
    if (players.length === 0) { toast.error('At least one player name required'); return; }
    setSaving(true);
    try {
      await sportsApi.post(`/sports/tournaments/${tournament.id}/teams`, {
        team_name: teamName.trim(), players, division_id: divId || null,
        logo_key: logoKey, logo_thumb_key: logoThumbKey,
      });
      toast.success('Team added');
      setNames(Array(size).fill('')); setTeamName(''); setDivId('');
      setLogoKey(null); setLogoThumbKey(null); setLogoPreview(null);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add team'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
      <p className="text-xs uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Add Team Manually ({size} player{size > 1 ? 's' : ''})</p>
      <div className="grid grid-cols-2 gap-2">
        {names.map((v, i) => (
          <input key={i} data-testid={`team-player${i + 1}`} value={v} onChange={e => setName(i, e.target.value)}
            placeholder={`Player ${i + 1}${i === 0 ? ' *' : ''}`} style={inputStyle} />
        ))}
        <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Team name (optional)" style={inputStyle} />
        {tournament.divisions?.length > 0 && (
          <select value={divId} onChange={e => setDivId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select division</option>
            {tournament.divisions.map(d => <option key={d.id} value={d.id}>{d.name}{d.max_teams ? ` (max ${d.max_teams})` : ''}</option>)}
          </select>
        )}
      </div>
      {/* Logo upload */}
      <div className="mt-2 flex items-center gap-3 p-2.5 rounded-lg" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: '#111', border: '1px solid #2A2A2A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {logoPreview
            ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 9, color: '#525252' }}>Logo</span>}
        </div>
        <div className="flex-1">
          <p className="text-xs" style={{ color: '#A3A3A3' }}>Team Logo <span style={{ color: '#525252' }}>(optional)</span></p>
          <label className="block mt-0.5 cursor-pointer">
            <input data-testid="add-team-logo-upload" type="file" accept="image/*" className="hidden"
              onChange={e => uploadLogo(e.target.files?.[0])} />
            <span className="inline-block text-xs px-2 py-1 rounded" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A', cursor: 'pointer' }}>
              {logoUploading ? 'Uploading…' : logoThumbKey ? '✓ Uploaded — change' : 'Choose image'}
            </span>
          </label>
        </div>
      </div>
      <button data-testid="add-team-btn" onClick={submit} disabled={saving || logoUploading}
        className="mt-3 w-full py-2 rounded-lg text-sm font-bold uppercase" style={{ background: '#E63946', color: '#fff', opacity: (saving || logoUploading) ? 0.6 : 1 }}>
        {saving ? 'Adding…' : '+ Add Team'}
      </button>
    </div>
  );
}

// ── Proof-of-payment viewer (carousel) ────────────────────────────────────────
function ProofViewerModal({ proofTeams, initialIndex, onClose, onCollect }) {
  const [idx, setIdx] = useState(initialIndex || 0);
  const backdrop = useBackdropClose(onClose);
  const tm = proofTeams[idx];
  if (!tm) return null;
  const prev = () => setIdx(i => Math.max(0, i - 1));
  const next = () => setIdx(i => Math.min(proofTeams.length - 1, i + 1));
  // Hand off to the reconcile modal (amount/method review) — no one-tap paid.
  const handlePaid = () => { onClose(); onCollect(tm); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)' }} {...backdrop}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-1 h-5 rounded-full shrink-0" style={{ background: RED }} />
            <div className="min-w-0">
              <p className="font-black text-sm" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{(tm.team_name || '').toUpperCase()}</p>
              <p className="text-xs truncate" style={{ color: '#737373' }}>{tm.players?.map(p => p.name).join(' / ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {proofTeams.length > 1 && (
              <span className="text-xs font-bold" style={{ color: '#525252' }}>{idx + 1} / {proofTeams.length}</span>
            )}
            <button onClick={onClose} style={{ color: '#525252' }}><X className="h-5 w-5" /></button>
          </div>
        </div>
        {/* Proof image */}
        <div className="relative" style={{ background: '#0A0A0A', minHeight: 260 }}>
          {tm.proof_url ? (
            <img src={tm.proof_url} alt="Payment proof"
              style={{ width: '100%', maxHeight: 420, objectFit: 'contain', display: 'block' }} />
          ) : (
            <div className="flex items-center justify-center h-64" style={{ color: '#525252' }}>
              <p className="text-sm">Image unavailable</p>
            </div>
          )}
          {/* Prev / Next arrows */}
          {proofTeams.length > 1 && (
            <>
              <button onClick={prev} disabled={idx === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all"
                style={{ background: 'rgba(0,0,0,0.6)', color: idx === 0 ? '#333' : '#fff', border: '1px solid #2A2A2A' }}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={next} disabled={idx === proofTeams.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all"
                style={{ background: 'rgba(0,0,0,0.6)', color: idx === proofTeams.length - 1 ? '#333' : '#fff', border: '1px solid #2A2A2A' }}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: '1px solid #1E1E1E' }}>
          <div>
            <p className="text-xs" style={{ color: '#525252' }}>Entry fee payment proof</p>
            <p className="text-sm font-bold" style={{ color: '#F59E0B' }}>Awaiting confirmation</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>Close</button>
            <button onClick={handlePaid} data-testid={`proof-collect-${tm.id}`}
              className="px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5"
              style={{ background: '#22C55E', color: '#0A0A0A' }}>
              <CheckCircle2 className="h-4 w-4" /> Review &amp; Record This Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function MatchCard({ m, divisions, slug, onReopen }) {
  const scorerUrl = `${window.location.origin}/sports/score/${m.qr_token}`;
  const historyUrl = slug ? `${window.location.origin}/book/${slug}/match/${m.id}` : '';
  const [showQR, setShowQR] = useState(false);
  const [showHistoryQR, setShowHistoryQR] = useState(false);
  const st = MATCH_STATUS[m.status] || MATCH_STATUS.pending;
  const divName = divisions?.find(d => d.id === m.division_id)?.name;
  const started = m.status === 'live' || m.status === 'completed';
  return (
    <div data-testid={`match-${m.match_number}`} className="rounded-xl p-4" style={{ background: '#0F0F0F', border: `1px solid ${m.status === 'live' ? '#E63946' : '#2A2A2A'}` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs" style={{ color: '#737373' }}>R{m.round} · Match {m.match_number}{divName ? ` · ${divName}` : ''}{m.bracket_type === 'third_place' ? ' · 🥉 3rd Place' : ''}</span>
        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${st.c}22`, color: st.c }}>{st.l}</span>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: m.winner_side === 'a' ? '#22C55E' : (m.status === 'completed' && m.winner_side === 'b' ? '#EF4444' : '#fff') }}>
            {m.winner_side === 'a' && <Crown className="inline h-3.5 w-3.5 mr-1" style={{ color: '#22C55E' }} />}
            {m.team_a_name || <span style={{ color: '#525252' }}>TBD</span>}
          </span>
          <span className="text-sm font-bold tabular-nums" style={{ color: '#fff' }}>{m.games_won_a} {m.status !== 'pending' && m.status !== 'bye' ? `(${m.score_a})` : ''}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: m.winner_side === 'b' ? '#22C55E' : (m.status === 'completed' && m.winner_side === 'a' ? '#EF4444' : '#fff') }}>
            {m.winner_side === 'b' && <Crown className="inline h-3.5 w-3.5 mr-1" style={{ color: '#22C55E' }} />}
            {m.team_b_name || <span style={{ color: '#525252' }}>TBD</span>}
          </span>
          <span className="text-sm font-bold tabular-nums" style={{ color: '#fff' }}>{m.games_won_b} {m.status !== 'pending' && m.status !== 'bye' ? `(${m.score_b})` : ''}</span>
        </div>
      </div>
      {m.court_name && <p className="text-xs mt-2" style={{ color: '#737373' }}>Court: {m.court_name}</p>}
      {onReopen && m.status === 'completed' && (
        <button data-testid={`reopen-match-${m.match_number}`} onClick={() => onReopen(m)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase"
          style={{ background: '#1A1A1A', border: '1px solid #3A3A3A', color: '#F59E0B' }}>
          <RotateCcw className="h-3.5 w-3.5" /> Correct Result
        </button>
      )}
      {m.status !== 'completed' && m.status !== 'bye' && m.team_a_id && m.team_b_id && (
        <div className="flex gap-2 mt-3">
          <a data-testid={`open-scorer-${m.match_number}`} href={scorerUrl} target="_blank" rel="noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase" style={{ background: '#E63946', color: '#fff' }}>
            <Play className="h-3.5 w-3.5" /> Open Scorer
          </a>
          <button onClick={() => setShowQR(v => !v)} className="px-3 rounded-lg" style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#A3A3A3' }}>
            <QrCode className="h-4 w-4" />
          </button>
        </div>
      )}
      {started && slug && (
        <button data-testid={`history-qr-btn-${m.match_number}`} onClick={() => setShowHistoryQR(v => !v)}
          className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#A3A3A3' }}>
          <QrCode className="h-3.5 w-3.5" /> {showHistoryQR ? 'Hide' : 'Match History QR'}
        </button>
      )}
      {showQR && (
        <div className="mt-3 flex flex-col items-center p-3 rounded-lg" style={{ background: '#fff' }}>
          <QRCodeSVG value={scorerUrl} size={140} level="H" includeMargin />
          <p className="text-xs mt-1" style={{ color: '#0A0A0A' }}>Scan to score this match</p>
        </div>
      )}
      {showHistoryQR && historyUrl && (
        <div className="mt-3 flex flex-col items-center p-3 rounded-lg" style={{ background: '#fff' }}>
          <QRCodeSVG value={historyUrl} size={140} level="H" includeMargin />
          <p className="text-xs mt-1 font-semibold" style={{ color: '#0A0A0A' }}>Spectator history — scan for full results</p>
          <a href={historyUrl} target="_blank" rel="noreferrer" className="text-[10px] mt-0.5 underline" style={{ color: '#2563EB' }}>Open history page</a>
        </div>
      )}
    </div>
  );
}

// Awards editor for announce-style events: per-division Main + Sub awards,
// each with prize text (public immediately) and a recipient declared when the
// tournament finishes. Saves through the regular tournament PUT.
function AwardsPanel({ t, onSaved }) {
  const [awards, setAwards] = useState(() => JSON.parse(JSON.stringify(t.awards || [])));
  const [saving, setSaving] = useState(false);
  const divName = (id) => (t.divisions || []).find(d => d.id === id)?.name || 'Event Awards';
  const groups = awards.length ? awards
    : ((t.divisions || []).length ? t.divisions.map(d => ({ division_id: d.id, main: [], sub: [] })) : [{ division_id: null, main: [], sub: [] }]);
  const patch = (gi, kind, i, field, v) => {
    const next = JSON.parse(JSON.stringify(groups));
    next[gi][kind][i][field] = v;
    setAwards(next);
  };
  const addEntry = (gi, kind) => {
    const next = JSON.parse(JSON.stringify(groups));
    next[gi][kind].push(kind === 'main'
      ? { rank: next[gi].main.length + 1, label: '', prize: '', recipient: '' }
      : { label: '', prize: '', recipient: '' });
    setAwards(next);
  };
  const removeEntry = (gi, kind, i) => {
    const next = JSON.parse(JSON.stringify(groups));
    next[gi][kind].splice(i, 1);
    setAwards(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await sportsApi.put(`/sports/tournaments/${t.id}`, { awards: groups });
      toast.success('Awards saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save awards');
    } finally { setSaving(false); }
  };
  const row = (gi, kind, e, i) => (
    <div key={`${kind}-${i}`} className="grid gap-2 mb-2" style={{ gridTemplateColumns: '1fr 1.4fr 1fr auto' }}>
      <input value={e.label} onChange={ev => patch(gi, kind, i, 'label', ev.target.value)} placeholder={kind === 'main' ? `Place ${i + 1}` : 'e.g. MVP'}
        style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 8, color: '#fff', fontSize: 13, padding: '8px 10px' }} />
      <input value={e.prize} onChange={ev => patch(gi, kind, i, 'prize', ev.target.value)} placeholder="Prize — e.g. ₱5,000 + Arronax Ball sponsorship"
        style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 8, color: '#fff', fontSize: 13, padding: '8px 10px' }} />
      <input value={e.recipient} onChange={ev => patch(gi, kind, i, 'recipient', ev.target.value)} placeholder="Winner (declare later)"
        style={{ background: '#0A0A0A', border: '1px solid #22C55E40', borderRadius: 8, color: '#22C55E', fontSize: 13, padding: '8px 10px' }} />
      <button onClick={() => removeEntry(gi, kind, i)} style={{ color: '#737373' }}><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div data-testid="awards-panel" className="space-y-5">
      {groups.map((g, gi) => (
        <div key={g.division_id || 'all'} className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <p className="text-sm font-bold uppercase mb-3" style={{ fontFamily: 'Oswald, sans-serif', color: RED }}>{divName(g.division_id)}</p>
          <p className="text-xs uppercase mb-2" style={{ color: '#737373', letterSpacing: 1 }}>Main awards</p>
          {g.main.map((e, i) => row(gi, 'main', e, i))}
          <button onClick={() => addEntry(gi, 'main')} className="text-xs font-bold mb-4" style={{ color: RED }}>+ Add main award</button>
          <p className="text-xs uppercase mb-2" style={{ color: '#737373', letterSpacing: 1 }}>Sub awards</p>
          {g.sub.map((e, i) => row(gi, 'sub', e, i))}
          <button onClick={() => addEntry(gi, 'sub')} className="text-xs font-bold" style={{ color: RED }}>+ Add sub award</button>
        </div>
      ))}
      <button data-testid="awards-save" onClick={save} disabled={saving}
        className="px-6 py-2.5 rounded-lg text-sm font-bold uppercase" style={{ background: RED, color: '#fff', opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Saving…' : 'Save Awards'}
      </button>
    </div>
  );
}

function QRCodesHub({ tournament, matches, slug, tournamentId }) {
  const origin = window.location.origin;
  const base = usePublicBase(slug);
  const isAnnounce = tournament.event_style === 'announce';
  const spectatorUrl = tournament.short_code && base
    ? `${base}/t/${tournament.short_code}/live`
    : `${origin}/book/${slug}/tournament/${tournamentId}/spectator`;
  // Announce events with an external registration site: the poster QR sends
  // players straight to that site (Reclub etc.), not to our public page.
  const registrationUrl = (isAnnounce && tournament.registration_url)
    ? tournament.registration_url
    : (tournament.short_code && base
      ? `${base}/t/${tournament.short_code}`
      : `${origin}/book/${slug}/tournament/${tournamentId}`);

  // Group matches by court for court-specific QRs
  const courtMatches = {};
  const activeMatches = matches.filter(m => m.status !== 'completed' && m.status !== 'bye' && m.team_a_name && m.team_b_name);
  activeMatches.forEach(m => {
    const court = m.court_name || 'Unassigned';
    if (!courtMatches[court]) courtMatches[court] = [];
    courtMatches[court].push(m);
  });

  return (
    <div data-testid="qr-codes-hub" className="space-y-6">
      {!isAnnounce && <CoordinatorQrPanel apiBase={`/sports/tournaments/${tournamentId}`} eventName={tournament.name} />}

      {/* Spectator Poster QR — meaningless without live matches */}
      {!isAnnounce && (
      <div className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
        <div className="flex items-center gap-2 mb-1">
          <Monitor className="h-4 w-4" style={{ color: '#FFB300' }} />
          <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#FFB300' }}>Spectator QR — Post at Venue</p>
        </div>
        <p className="text-xs mb-4" style={{ color: '#555' }}>Spectators scan this to follow live scores, brackets & standings on their phone.</p>
        <div className="flex flex-col items-center">
          <div className="p-4 rounded-xl mb-3" style={{ background: '#fff' }}>
            <QRCodeSVG id="qr-spectator" value={spectatorUrl} size={180} level="H" includeMargin
              data-label={tournament.name} data-sublabel="Scan for LIVE scores & brackets" />
          </div>
          <p className="text-xs font-bold mb-3" style={{ color: '#A3A3A3' }}>Scan for LIVE scores & brackets</p>
          <button data-testid="download-spectator-qr" onClick={() => downloadQR('qr-spectator', `spectator-${tournament.name.replace(/\s+/g, '-')}.png`)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#FFB30018', color: '#FFB300', border: '1px solid #FFB30030' }}>
            <Download className="h-3.5 w-3.5" /> Download Poster QR
          </button>
        </div>
      </div>
      )}

      {/* Registration QR */}
      <div className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-4 w-4" style={{ color: '#22C55E' }} />
          <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#22C55E' }}>Registration QR</p>
        </div>
        <p className="text-xs mb-4" style={{ color: '#555' }}>Players scan to register their team for this tournament.</p>
        <div className="flex flex-col items-center">
          <div className="p-4 rounded-xl mb-3" style={{ background: '#fff' }}>
            <QRCodeSVG id="qr-registration" value={registrationUrl} size={160} level="H" includeMargin
              data-label={tournament.name} data-sublabel="Scan to register your team" />
          </div>
          <button data-testid="download-reg-qr" onClick={() => downloadQR('qr-registration', `register-${tournament.name.replace(/\s+/g, '-')}.png`)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#22C55E18', color: '#22C55E', border: '1px solid #22C55E30' }}>
            <Download className="h-3.5 w-3.5" /> Download Registration QR
          </button>
        </div>
      </div>

      {/* Scorer QRs — per court (bracket-managed events only) */}
      {!isAnnounce && (
      <div className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
        <div className="flex items-center gap-2 mb-1">
          <Play className="h-4 w-4" style={{ color: RED }} />
          <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: RED }}>Scorer QR Codes — Print & Place on Courts</p>
        </div>
        <p className="text-xs mb-4" style={{ color: '#555' }}>Print one per court. Referee walks to their court, scans the QR, and starts scoring.</p>
        {Object.keys(courtMatches).length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#444' }}>No active matches with court assignments yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(courtMatches).map(([court, cms]) => (
              <div key={court} className="rounded-xl p-4" style={{ background: '#0A0A0A', border: '1px solid #1A1A1A' }}>
                <p className="text-xs font-bold uppercase mb-1" style={{ color: RED }}>{court}</p>
                {cms.slice(0, 1).map(m => {
                  const scorerUrl = `${origin}/sports/score/${m.qr_token}`;
                  const qrId = `qr-scorer-${m.match_number}`;
                  return (
                    <div key={m.id}>
                      <p className="text-sm font-semibold mb-2" style={{ color: '#ccc' }}>
                        M{m.match_number}: {m.team_a_name || 'TBD'} vs {m.team_b_name || 'TBD'}
                      </p>
                      <div className="flex flex-col items-center">
                        <div className="p-3 rounded-lg mb-2" style={{ background: '#fff' }}>
                          <QRCodeSVG id={qrId} value={scorerUrl} size={120} level="H" includeMargin
                            data-label={`${court} — Match ${m.match_number}`} data-sublabel={`${m.team_a_name} vs ${m.team_b_name}`} />
                        </div>
                        <button onClick={() => downloadQR(qrId, `scorer-${court}-M${m.match_number}.png`)}
                          data-testid={`download-scorer-qr-${m.match_number}`}
                          className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded"
                          style={{ background: '#E6394618', color: RED, border: '1px solid #E6394630' }}>
                          <Download className="h-3 w-3" /> Download
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

const PAY_STATUS_UI = {
  paid:             { color: '#22C55E', label: 'Paid', icon: CircleCheck },
  deposit_paid:     { color: '#3B82F6', label: '50% Paid', icon: Clock },
  partial:          { color: '#8B5CF6', label: 'Partial', icon: Clock },
  awaiting_payment: { color: '#8B5CF6', label: 'Online ⏳', icon: Clock },
  pending:          { color: '#FFB300', label: 'Pending', icon: Clock },
  at_venue:         { color: '#2979FF', label: 'Pay at Venue', icon: AlertCircle },
  free:             { color: '#737373', label: 'Free', icon: CircleCheck },
};

function TournamentDashboard({ tournamentId, tournament }) {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sportsApi.get(`/sports/tournaments/${tournamentId}/dashboard`)
      .then(r => setDash(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 rounded-full" style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} /></div>;
  if (!dash) return null;

  const hasFees = dash.entry_fee > 0;
  const pct = dash.revenue.total_expected > 0 ? Math.round((dash.revenue.collected / dash.revenue.total_expected) * 100) : 0;

  return (
    <div data-testid="tournament-dashboard" className="space-y-5">
      {/* Revenue cards */}
      {hasFees && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Collected', value: `₱${dash.revenue.collected.toLocaleString()}`, color: '#22C55E', sub: `${dash.payments.paid} teams paid` },
            { label: 'Pending', value: `₱${dash.revenue.pending.toLocaleString()}`, color: '#FFB300', sub: `${dash.payments.pending} teams unpaid` },
            { label: 'Expected Total', value: `₱${dash.revenue.total_expected.toLocaleString()}`, color: '#fff', sub: `${dash.total_teams} teams × ₱${dash.entry_fee}` },
            { label: 'Collection Rate', value: `${pct}%`, color: pct >= 80 ? '#22C55E' : pct >= 50 ? '#FFB300' : '#EF4444', sub: `${dash.payments.paid}/${dash.total_teams} paid` },
          ].map(({ label, value, color, sub }) => (
            <div key={label} className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#555' }}>{label}</p>
              <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Oswald, sans-serif', color }}>{value}</p>
              <p className="text-xs mt-1" style={{ color: '#444' }}>{sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {hasFees && (
        <div className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide font-bold" style={{ color: '#555' }}>Payment Progress</p>
            <p className="text-xs font-bold" style={{ color: '#22C55E' }}>{dash.payments.paid}/{dash.total_teams}</p>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#1A1A1A', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: pct >= 80 ? '#22C55E' : pct >= 50 ? '#FFB300' : '#EF4444', transition: 'width 0.4s' }} />
          </div>
        </div>
      )}

      {/* Match progress */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Matches Completed', value: dash.matches.completed, color: '#22C55E' },
          { label: 'Live Now', value: dash.matches.live, color: '#E63946' },
          { label: 'Pending', value: dash.matches.pending, color: '#737373' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl p-3 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color }}>{value}</p>
            <p className="text-xs" style={{ color: '#555' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Registered per division */}
      {(tournament.divisions?.length > 0) && (
        <div>
          <p className="text-xs uppercase tracking-wide font-bold mb-3" style={{ color: '#555' }}>Registered Per Division</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {tournament.divisions.map(d => {
              const count = dash.teams.filter(tm => tm.division_id === d.id).length;
              return (
                <div key={d.id} data-testid={`dash-division-${d.id}`} className="rounded-xl p-3" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                  <p className="text-xs uppercase font-bold truncate" style={{ color: '#E63946' }}>{d.name}</p>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 26, fontWeight: 900, color: '#fff' }}>{count}</p>
                  <p className="text-xs" style={{ color: '#444' }}>team{count !== 1 ? 's' : ''}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team payment list */}
      {hasFees && (
        <div>
          <p className="text-xs uppercase tracking-wide font-bold mb-3" style={{ color: '#555' }}>Team Payment Status</p>
          <div className="space-y-4">
            {groupByDivision(dash.teams, tournament.divisions).map(g => (
              <div key={g.divId || 'all'}>
                {g.divName && <p className="text-xs uppercase font-bold mb-2 px-1" style={{ color: '#E63946' }}>{g.divName}</p>}
                <div className="space-y-1.5">
            {g.matches.map(tm => {
              const st = PAY_STATUS_UI[tm.payment_status] || PAY_STATUS_UI.free;
              const StIcon = st.icon;
              return (
                <div key={tm.id} data-testid={`dash-team-${tm.id}`}
                  className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#0F0F0F', border: '1px solid #1A1A1A' }}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: '#fff' }}>{tm.team_name}</p>
                    <p className="text-xs" style={{ color: '#555' }}>
                      {tm.players?.map(p => p.name).join(' / ')}
                      {tm.registered_by === 'invitation' ? ' · invited' : tm.registered_by === 'public' ? ' · self-registered' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {tm.amount_due > 0 && <span className="text-xs font-bold" style={{ color: '#888' }}>₱{tm.amount_due.toLocaleString()}</span>}
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: st.color + '18', color: st.color }}>
                      <StIcon className="h-3 w-3" /> {st.label}
                    </span>
                  </div>
                </div>
              );
            })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No fees message */}
      {!hasFees && (
        <div className="rounded-xl p-8 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <DollarSign className="h-8 w-8 mx-auto mb-2" style={{ color: '#2A2A2A' }} />
          <p className="text-sm" style={{ color: '#737373' }}>This is a free tournament — no entry fees to track</p>
          <p className="text-xs mt-1" style={{ color: '#444' }}>{dash.total_teams} teams registered · {dash.matches.total} matches</p>
        </div>
      )}
    </div>
  );
}

const INV_STATUS = {
  pending:   { color: '#FFB300', label: 'Pending' },
  accepted:  { color: '#22C55E', label: 'Accepted' },
  declined:  { color: '#EF4444', label: 'Declined' },
  expired:   { color: '#737373', label: 'Expired' },
  cancelled: { color: '#737373', label: 'Cancelled' },
};

function InvitationsPanel({ tournamentId, invitations, bracketGenerated, divisions, onRefresh }) {
  const emptyRow = () => ({ team_name: '', email: '', division_id: '' });
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const divName = (id) => divisions?.find(d => d.id === id)?.name;

  const updateRow = (i, key, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRow = () => setRows(rs => [...rs, emptyRow()]);
  const removeRow = (i) => setRows(rs => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);
  const addRowsBulk = (n) => setRows(rs => [...rs, ...Array.from({ length: n }, emptyRow)]);

  const norm = (s) => (s || '').trim().toLowerCase();
  const existingEmails = new Set(invitations.filter(i => ['pending', 'accepted'].includes(i.status)).map(i => norm(i.email)));

  // Per-row duplicate detection (within the list + against already-sent invites).
  const dupNote = (i) => {
    const r = rows[i];
    const e = norm(r.email);
    if (!e) return null;
    const firstIdx = rows.findIndex(x => norm(x.email) === e);
    if (firstIdx !== -1 && firstIdx < i) return `Same email as row ${firstIdx + 1}`;
    if (existingEmails.has(e)) return 'This email was already invited';
    return null;
  };

  const send = async () => {
    const filled = rows.map((r, i) => ({ ...r, _i: i })).filter(r => r.team_name.trim() && r.email.trim());
    if (filled.length === 0) { toast.error('Add at least one team name + email'); return; }
    if (divisions?.length > 0) {
      const missingDiv = filled.find(r => !r.division_id);
      if (missingDiv) { toast.error(`Row ${missingDiv._i + 1}: please select a division`); return; }
    }
    // Smart duplicate confirmation
    const dups = filled.map(r => ({ row: r._i + 1, note: dupNote(r._i), email: r.email })).filter(d => d.note);
    if (dups.length > 0) {
      const msg = 'Possible duplicates detected:\n\n' +
        dups.map(d => `• Row ${d.row} (${d.email}) — ${d.note}`).join('\n') +
        '\n\nIs this intentional? Click OK to send anyway, or Cancel to fix them first.';
      if (!window.confirm(msg)) return;
    }
    setSending(true); setResults(null);
    try {
      const r = await sportsApi.post(`/sports/tournaments/${tournamentId}/invitations`, {
        invitations: filled.map(r => ({ team_name: r.team_name.trim(), email: r.email.trim(), division_id: r.division_id || null })),
      });
      const res = r.data.results || [];
      setResults(res);
      const sent = res.filter(x => x.status === 'sent').length;
      const skipped = res.filter(x => x.status === 'skipped').length;
      if (sent) toast.success(`${sent} invitation${sent > 1 ? 's' : ''} sent${skipped ? `, ${skipped} skipped` : ''}`);
      else toast.error(skipped ? `All ${skipped} skipped — see details` : 'Nothing sent');
      // Keep only the rows that were skipped (match by index — backend preserves order)
      // so the owner can fix them; clear everything on full success.
      const remaining = filled
        .filter((_, idx) => res[idx] && res[idx].status === 'skipped')
        .map(r => ({ team_name: r.team_name, email: r.email, division_id: r.division_id }));
      setRows(remaining.length ? remaining : [emptyRow(), emptyRow(), emptyRow()]);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const cancelInvite = async (invId) => {
    setCancelling(invId);
    try {
      await sportsApi.delete(`/sports/tournaments/${tournamentId}/invitations/${invId}`);
      toast.success('Invitation cancelled');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCancelling(null); }
  };

  const pending = invitations.filter(i => i.status === 'pending');
  const accepted = invitations.filter(i => i.status === 'accepted');
  const other = invitations.filter(i => !['pending', 'accepted'].includes(i.status));
  const filledCount = rows.filter(r => r.team_name.trim() && r.email.trim()).length;

  return (
    <div className="space-y-5">
      {/* Bulk invitation form */}
      {!bracketGenerated && (
        <div data-testid="send-invitation-form" className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4" style={{ color: '#FFB300' }} />
              <p className="text-xs uppercase tracking-wide font-bold" style={{ color: '#FFB300' }}>Invite Teams (bulk)</p>
            </div>
            <span className="text-xs" style={{ color: '#525252' }}>{filledCount} ready · up to 50</span>
          </div>

          <div className="space-y-2">
            {rows.map((r, i) => {
              const note = dupNote(i);
              return (
                <div key={i} data-testid={`invite-row-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold tabular-nums pt-2.5" style={{ color: '#525252', width: 18 }}>{i + 1}</span>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input data-testid={`invite-team-name-${i}`} value={r.team_name} onChange={e => updateRow(i, 'team_name', e.target.value)} placeholder="Team name" style={inputStyle} />
                      <input data-testid={`invite-email-${i}`} value={r.email} onChange={e => updateRow(i, 'email', e.target.value)} placeholder="team@email.com" type="email" style={{ ...inputStyle, borderColor: note ? '#FFB30066' : '#2A2A2A' }} />
                      {divisions?.length > 0 && (
                        <select data-testid={`invite-division-${i}`} value={r.division_id} onChange={e => updateRow(i, 'division_id', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                          <option value="">Select division *</option>
                          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      )}
                    </div>
                    <button data-testid={`invite-remove-${i}`} onClick={() => removeRow(i)} title="Remove row" className="pt-2.5" style={{ color: '#525252' }}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {note && (
                    <p data-testid={`invite-dup-${i}`} className="text-xs ml-6 mt-0.5 flex items-center gap-1" style={{ color: '#FFB300' }}>
                      <AlertCircle className="h-3 w-3" /> {note}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-3">
            <button data-testid="invite-add-row" onClick={addRow} className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg" style={{ background: '#1A1A1A', color: '#FFB300', border: '1px solid #2A2A2A' }}>
              <Plus className="h-3.5 w-3.5" /> Add row
            </button>
            <button data-testid="invite-add-10" onClick={() => addRowsBulk(10)} className="text-xs font-bold px-3 py-2 rounded-lg" style={{ background: '#1A1A1A', color: '#737373', border: '1px solid #2A2A2A' }}>
              +10 rows
            </button>
          </div>

          <button data-testid="send-invite-btn" onClick={send} disabled={sending || filledCount === 0}
            className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold uppercase"
            style={{ background: '#FFB300', color: '#0A0A0A', opacity: (sending || filledCount === 0) ? 0.5 : 1, cursor: (sending || filledCount === 0) ? 'not-allowed' : 'pointer' }}>
            <Mail className="h-4 w-4" /> {sending ? 'Sending…' : `Send ${filledCount || ''} Invitation${filledCount === 1 ? '' : 's'}`}
          </button>

          {/* Results summary */}
          {results && (
            <div data-testid="invite-results" className="mt-3 rounded-lg p-3 space-y-1" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
              <p className="text-xs font-bold uppercase mb-1" style={{ color: '#737373' }}>Send results</p>
              {results.map((x, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span style={{ color: '#A3A3A3' }}>{x.team_name || x.email || '(row)'} <span style={{ color: '#555' }}>· {x.email}</span></span>
                  <span className="font-bold" style={{ color: x.status === 'sent' ? '#22C55E' : '#FFB300' }}>
                    {x.status === 'sent' ? '✓ Sent' : `⚠ Skipped${x.reason ? ` — ${x.reason}` : ''}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary counters */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending', count: pending.length, color: '#FFB300' },
          { label: 'Accepted', count: accepted.length, color: '#22C55E' },
          { label: 'Declined / Expired', count: other.length, color: '#737373' },
        ].map(({ label, count, color }) => (
          <div key={label} className="rounded-xl p-3 text-center" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color }}>{count}</p>
            <p className="text-xs" style={{ color: '#737373' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Invitation list */}
      <div className="space-y-2">
        {invitations.length === 0 && (
          <div className="rounded-xl p-8 text-center" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
            <Mail className="h-8 w-8 mx-auto mb-2" style={{ color: '#2A2A2A' }} />
            <p className="text-sm" style={{ color: '#737373' }}>No invitations sent yet</p>
          </div>
        )}
        {invitations.map(inv => {
          const st = INV_STATUS[inv.status] || INV_STATUS.pending;
          return (
            <div key={inv.id} data-testid={`invitation-${inv.id}`}
              className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#0F0F0F', border: `1px solid ${inv.status === 'pending' ? '#FFB30030' : '#2A2A2A'}` }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold" style={{ color: '#fff' }}>{inv.team_name}</p>
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ background: st.color + '22', color: st.color }}>{st.label}</span>
                  {inv.division_id && divName(inv.division_id) && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ background: '#E6394618', color: '#E63946' }}>{divName(inv.division_id)}</span>
                  )}
                </div>
                <p className="text-xs" style={{ color: '#555' }}>{inv.email}</p>
                {inv.responded_at && <p className="text-xs" style={{ color: '#444' }}>Responded: {fmtDate(inv.responded_at)}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(inv.status === 'pending' || inv.status === 'accepted') && !bracketGenerated && (
                  <button data-testid={`cancel-invite-${inv.id}`} onClick={() => cancelInvite(inv.id)}
                    disabled={cancelling === inv.id}
                    className="text-xs px-2 py-1 rounded" style={{ background: '#1A1A1A', color: '#EF4444', border: '1px solid #2A2A2A' }}>
                    {cancelling === inv.id ? '…' : 'Cancel'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SportsTournamentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useSportsAuth();
  const [t, setT] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState({});
  // Set after the tournament loads: live tournaments land on the bracket,
  // everything else on the Dashboard (same convention as open play).
  // ?tab=players deep-links straight to the money desk (used by pay-return).
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'players' ? 'players' : '');
  // Announce events hide bracket/players tabs in some modes — snap any stale
  // tab choice (deep link or in_progress default) back to the dashboard.
  useEffect(() => {
    if (!t || t.event_style !== 'announce') return;
    const visible = ['dashboard', 'awards', 'qrcodes',
      ...(t.announce_registration === 'onsite' ? ['players', 'teams'] : [])];
    if (tab && !visible.includes(tab)) setTab('dashboard');
  }, [t, tab]);
  const [bracketView, setBracketView] = useState('bracket');
  const [loading, setLoading] = useState(true);
  const [genLoading, setGenLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [qrMatch, setQrMatch] = useState(null);
  const [proofViewer, setProofViewer] = useState(null); // { teams: [...], index: 0 }
  const [paylinkTeam, setPaylinkTeam] = useState(null);
  const { lightboxUrl, openLightbox, closeLightbox } = useLogoLightbox();
  const logoMap = buildLogoMap(teams);

  const slug = organization?.slug;
  const pubBase = usePublicBase(slug);
  const regUrl = !slug ? '' : (t?.short_code && pubBase
    ? `${pubBase}/t/${t.short_code}`
    : `${window.location.origin}/book/${slug}/tournament/${id}`);

  const fetchAll = useCallback(() => {
    sportsApi.get(`/sports/tournaments/${id}`).then(r => {
      setT(r.data);
      setTab(prev => prev || (r.data.event_style !== 'announce' && r.data.status === 'in_progress' ? 'bracket' : 'dashboard'));
    }).catch(() => { toast.error('Not found'); navigate('/sports/tournaments'); });
    sportsApi.get(`/sports/tournaments/${id}/teams`).then(r => setTeams(r.data || [])).catch(() => {});
    sportsApi.get(`/sports/tournaments/${id}/matches`).then(r => setMatches(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    sportsApi.get(`/sports/tournaments/${id}/invitations`).then(r => setInvitations(r.data || [])).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh while the tournament is accepting registrations or live —
  // keeps teams, invitations & matches in sync without a manual page reload.
  useEffect(() => {
    const active = ['registration_open', 'registration_closed', 'in_progress'].includes(t?.status);
    if (!active) return;
    const announce = t?.event_style === 'announce';
    const iv = setInterval(() => {
      sportsApi.get(`/sports/tournaments/${id}`).then(r => setT(r.data)).catch(() => {});
      sportsApi.get(`/sports/tournaments/${id}/teams`).then(r => setTeams(r.data || [])).catch(() => {});
      if (!announce) {
        sportsApi.get(`/sports/tournaments/${id}/matches`).then(r => setMatches(r.data || [])).catch(() => {});
        sportsApi.get(`/sports/tournaments/${id}/invitations`).then(r => setInvitations(r.data || [])).catch(() => {});
      }
    }, announce ? 20000 : 8000);
    return () => clearInterval(iv);
  }, [t?.status, t?.event_style, id]);

  // Every collection goes through the review-then-confirm reconcile modal —
  // no one-tap "mark paid" anywhere on this page (parity with open play).
  const [reconcile, setReconcile] = useState(null); // { players, index, initial? }
  const openCollect = (team, initial = null) => {
    const payable = teams.filter(tm => tm.payment_status !== 'free');
    const idx = Math.max(0, payable.findIndex(tm => tm.id === team.id));
    setProofViewer(null);
    setReconcile({ players: payable, index: idx, initial });
  };
  const openProofViewer = (allTeams, clickedTeam) => {
    const proofTeams = allTeams.filter(t => t.payment_status === 'pending' && t.proof_url);
    if (!proofTeams.length) return;
    const idx = proofTeams.findIndex(t => t.id === clickedTeam.id);
    setProofViewer({ teams: proofTeams, index: Math.max(0, idx) });
  };
  useEffect(() => {
    if (t?.format === 'round_robin' && slug) {
      sportsApi.get(`/sports/public/${slug}/tournaments/${id}`).then(r => setStandings(r.data.standings || {})).catch(() => {});
    }
  }, [t, matches, slug, id]);

  const setStatus = async (status) => {
    try { await sportsApi.put(`/sports/tournaments/${id}/status`, { status }); toast.success('Status updated'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  // Confirm-gated registration toggles (parity with Open Play)
  const [confirmModal, setConfirmModal] = useState(null);
  const askStatus = (status) => {
    const copy = {
      registration_open: {
        title: t?.status === 'registration_closed' ? 'Reopen Registration' : 'Open Registration',
        color: '#22C55E',
        confirmLabel: t?.status === 'registration_closed' ? 'Reopen' : 'Open Registration',
        message: t?.status === 'registration_closed'
          ? 'Reopen public registration? Teams will be able to sign up again through the public link.'
          : 'Open public registration? The tournament will appear on your booking page and anyone with the link can register a team.',
      },
      registration_closed: {
        title: 'Close Registration', color: '#F59E0B', confirmLabel: 'Close Registration',
        message: 'Close public registration? New teams won\'t be able to self-register (you can still add teams manually). You can reopen it anytime.',
      },
    }[status];
    if (!copy) return setStatus(status);
    setConfirmModal({ ...copy, onConfirm: () => { setConfirmModal(null); setStatus(status); } });
  };
  const removeTeam = (tm) => {
    const paid = Number(tm.amount_paid || 0);
    setConfirmModal({
      title: 'Remove Team',
      color: '#EF4444',
      confirmLabel: 'Remove Team',
      message: (
        <>
          Remove <b style={{ color: '#fff' }}>{tm.team_name}</b> from the tournament?
          {paid > 0 && (
            <span className="block mt-2 text-xs font-bold" style={{ color: '#F59E0B' }}>
              ⚠ This team has ₱{paid.toLocaleString()} in recorded payments. Removing them does NOT
              refund or erase that money — it stays in the books. Settle any refund at the desk first.
            </span>
          )}
        </>
      ),
      onConfirm: async () => {
        setConfirmModal(null);
        try { await sportsApi.delete(`/sports/tournaments/${id}/teams/${tm.id}`); fetchAll(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      },
    });
  };
  const [pinModal, setPinModal] = useState(null); // { mode:'generate'|'cancel', pin, error }
  const [seedModal, setSeedModal] = useState(false);
  const [manualSeeds, setManualSeeds] = useState({});  // { team_id: seed_number }
  const hasScores = matches.some(m => ['live', 'completed'].includes(m.status) || m.score_a > 0 || m.score_b > 0 || m.games_won_a > 0 || m.games_won_b > 0);

  const requestGenerate = () => {
    if (t.scorer_pin) setPinModal({ mode: 'generate', pin: '', error: '' });
    else doGenerate('');
  };
  const doGenerate = async (pin) => {
    setGenLoading(true);
    try {
      const body = { pin };
      const seedEntries = Object.entries(manualSeeds).filter(([_, v]) => v !== '' && v !== null);
      if (seedEntries.length > 0) {
        body.manual_seeds = Object.fromEntries(seedEntries.map(([k, v]) => [k, Number(v)]));
      }
      const r = await sportsApi.post(`/sports/tournaments/${id}/generate-bracket`, body);
      toast.success(`Bracket generated (${r.data.matches} matches)${seedEntries.length ? ' with manual seeds' : ''}`);
      setPinModal(null); setSeedModal(false); setTab('bracket'); fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to generate';
      setPinModal(pm => pm ? { ...pm, error: msg } : pm);
      if (!pinModal) toast.error(msg);
    } finally { setGenLoading(false); }
  };
  const doCancel = async (pin) => {
    setGenLoading(true);
    try {
      await sportsApi.post(`/sports/tournaments/${id}/cancel-bracket`, { pin });
      toast.success('Bracket cancelled — registration reopened');
      setPinModal(null); setTab('teams'); fetchAll();
    } catch (e) {
      setPinModal(pm => pm ? { ...pm, error: e.response?.data?.detail || 'Failed to cancel' } : pm);
    } finally { setGenLoading(false); }
  };
  const generate = requestGenerate;

  const poolMatches = matches.filter(m => m.stage === 'pool');
  const bracketMatches = matches.filter(m => m.stage !== 'pool');
  const poolMatchesRemaining = poolMatches.filter(m => !['completed', 'bye', 'cancelled'].includes(m.status)).length;

  const requestAdvance = () => {
    if (poolMatchesRemaining > 0) return;
    setPinModal({ mode: 'advance', pin: '', error: '' });
  };
  const doAdvance = async (pin) => {
    setGenLoading(true);
    try {
      await sportsApi.post(`/sports/tournaments/${id}/advance-to-bracket`, { pin });
      toast.success('Knockout bracket created from pool standings');
      setPinModal(null); setTab('bracket'); fetchAll();
    } catch (e) {
      setPinModal(pm => pm ? { ...pm, error: e.response?.data?.detail || 'Failed to advance' } : pm);
    } finally { setGenLoading(false); }
  };
  const deleteTournament = async () => {
    if (!window.confirm('Delete this tournament and all its data?')) return;
    try { await sportsApi.delete(`/sports/tournaments/${id}`); toast.success('Deleted'); navigate('/sports/tournaments'); }
    catch { toast.error('Failed'); }
  };
  const requestReopen = (m) => {
    if (t.scorer_pin) { setPinModal({ mode: 'reopen', pin: '', error: '', matchId: m.id, matchNo: m.match_number }); return; }
    // No tournament PIN set — still confirm before reopening a finished match.
    setConfirmModal({
      title: `Correct Result · Match ${m.match_number}`,
      color: '#F59E0B',
      confirmLabel: 'Reopen Match',
      message: 'Reopen this finished match so it can be re-scored? The winner is removed from the next round (blocked if that match already started).',
      onConfirm: () => { setConfirmModal(null); doReopen('', m.id); },
    });
  };
  const doReopen = async (pin, matchId) => {
    setGenLoading(true);
    try {
      await sportsApi.post(`/sports/tournaments/${id}/matches/${matchId}/reopen`, { pin });
      toast.success('Result reopened — re-score the match to set the correct winner');
      setPinModal(null); fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to reopen';
      setPinModal(pm => pm ? { ...pm, error: msg } : pm);
      if (!t.scorer_pin) toast.error(msg);
    } finally { setGenLoading(false); }
  };
  const copyReg = () => { navigator.clipboard.writeText(regUrl); toast.success('Registration link copied'); };

  if (loading || !t) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>Loading…</div>;

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <SportsNavBar />
      <div className="max-w-5xl mx-auto px-5 py-6">
        <button onClick={() => navigate('/sports/tournaments')} className="flex items-center gap-1.5 text-sm mb-4" style={{ color: '#737373' }}>
          <ArrowLeft className="h-4 w-4" /> All Tournaments
        </button>

        {/* Header */}
        <div className="rounded-2xl p-5 mb-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-5 w-5" style={{ color: '#E63946' }} />
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{t.name}</h1>
                <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${STATUS_COLORS[t.status]}22`, color: STATUS_COLORS[t.status] }}>{(t.status || 'draft').replace(/_/g, ' ')}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs" style={{ color: '#737373' }}>
                <span>{t.format === 'round_robin' ? 'Round Robin' : t.format === 'double_elimination' ? 'Double Elimination' : 'Single Elimination'}</span>
                <span>{t.game_mode === 'custom' ? `${t.team_size} per team` : (t.game_mode || 'doubles').replace('_', ' ')}</span>
                {t.event_style !== 'announce' && <span>Best of {t.best_of}, to {t.scoring_target || '∞'}{t.scoring_target ? ` (win by ${t.win_by})` : ''}</span>}
                {(t.event_style !== 'announce' || t.announce_registration === 'onsite') && <span>{teams.length} teams{t.max_teams ? ` / ${t.max_teams}` : ''}</span>}
                {t.entry_fee > 0 && <span style={{ color: '#22C55E' }}>₱{t.entry_fee} entry</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {t.status === 'draft' && <button data-testid="open-reg-btn" onClick={() => askStatus('registration_open')} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#22C55E', color: '#0A0A0A' }}>Open Registration</button>}
              {t.status === 'registration_open' && <button data-testid="close-reg-btn" onClick={() => askStatus('registration_closed')} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#F59E0B', color: '#0A0A0A' }}>Close Registration</button>}
              {t.status === 'registration_closed' && <button data-testid="reopen-reg-btn" onClick={() => askStatus('registration_open')} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>Reopen</button>}
              {t.event_style === 'announce' && ['registration_open', 'registration_closed'].includes(t.status) && (
                <button data-testid="announce-complete-btn" onClick={() => askStatus('completed')} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#3B82F6', color: '#fff' }}>Mark Completed</button>
              )}
              {slug && <a href={regUrl} target="_blank" rel="noreferrer" data-testid="tn-public-page-btn" className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}><ExternalLink className="h-3.5 w-3.5" /> Public Page</a>}
              {/* Hide/Show on booking site */}
              <button data-testid="tn-toggle-hidden"
                onClick={async () => {
                  const archived = !t.archived;
                  try {
                    await sportsApi.post(`/sports/tournaments/${id}/archive`, { archived });
                    toast.success(archived ? 'Tournament hidden from booking site' : 'Tournament visible on booking site');
                    fetchAll();
                  } catch (e) { toast.error('Failed to update visibility'); }
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: t.archived ? '#22C55E18' : '#1A1A1A', color: t.archived ? '#22C55E' : '#737373', border: `1px solid ${t.archived ? '#22C55E44' : '#2A2A2A'}` }}>
                {t.archived ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {t.archived ? 'Show on Site' : 'Hide from Site'}
              </button>
              {slug && t.bracket_generated && (
                <a href={`/book/${slug}/tournament/${id}/spectator`} target="_blank" rel="noreferrer" data-testid="spectator-mode-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#E6394618', color: '#E63946', border: '1px solid #E6394630' }}>
                  <Monitor className="h-3.5 w-3.5" /> Big Screen
                </a>
              )}
              {t.bracket_generated && !hasScores && (
                <button data-testid="cancel-bracket-btn" onClick={() => setPinModal({ mode: 'cancel', pin: '', error: '' })}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: '#1A1A1A', color: '#F59E0B', border: '1px solid #F59E0B40' }}>
                  <RefreshCw className="h-3.5 w-3.5" /> Cancel & Reopen
                </button>
              )}
              <button onClick={deleteTournament} className="px-3 py-1.5 rounded-lg text-xs" style={{ background: '#1A1A1A', color: '#E63946', border: '1px solid #2A2A2A' }}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          {confirmModal && <ConfirmModal {...confirmModal} onCancel={() => setConfirmModal(null)} />}

          {t.event_style !== 'announce' && !t.bracket_generated && teams.length >= 2 && (
            <div className="mt-4 space-y-2">
              {(t.format === 'single_elimination' || t.format === 'double_elimination') && !t.pool_play && (
                <button data-testid="set-seeds-btn" onClick={() => setSeedModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold uppercase"
                  style={{ background: '#141414', color: '#E63946', border: '1px solid #E6394640', fontFamily: 'Oswald, sans-serif' }}>
                  <Zap className="h-3.5 w-3.5" />
                  {Object.keys(manualSeeds).filter(k => manualSeeds[k]).length > 0
                    ? `Manual Seeds Set (${Object.keys(manualSeeds).filter(k => manualSeeds[k]).length}) — Edit`
                    : 'Set Manual Seeds (optional)'}
                </button>
              )}
              <button data-testid="generate-bracket-btn" onClick={generate} disabled={genLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold uppercase"
                style={{ background: '#E63946', color: '#fff', opacity: genLoading ? 0.6 : 1 }}>
                <Zap className="h-4 w-4" /> {genLoading ? 'Working…' : (t.pool_play ? 'Generate Pools' : 'Generate Bracket')}
              </button>
            </div>
          )}
          {t.pool_play && t.stage === 'pools' && (
            <button data-testid="advance-bracket-btn" onClick={requestAdvance} disabled={genLoading || poolMatchesRemaining > 0}
              title={poolMatchesRemaining > 0 ? `${poolMatchesRemaining} pool match(es) remaining` : ''}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold uppercase"
              style={{ background: poolMatchesRemaining > 0 ? '#1A1A1A' : '#22C55E', color: poolMatchesRemaining > 0 ? '#666' : '#0A0A0A', border: poolMatchesRemaining > 0 ? '1px solid #2A2A2A' : 'none', cursor: poolMatchesRemaining > 0 ? 'not-allowed' : 'pointer' }}>
              <GitBranch className="h-4 w-4" /> {poolMatchesRemaining > 0 ? `Finish ${poolMatchesRemaining} pool match(es) to advance` : 'Advance to Knockout'}
            </button>
          )}
        </div>

        {/* Outstanding money pill — one tap to the Payments desk (parity with open play) */}
        {(() => {
          const owing = teams.filter(tm => tm.payment_status !== 'free' && balanceOf(tm) > 0);
          const owingTotal = owing.reduce((s, tm) => s + balanceOf(tm), 0);
          if (owingTotal <= 0 || tab === 'players') return null;
          return (
            <button data-testid="t-outstanding-pill" onClick={() => setTab('players')}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl mb-4 text-sm font-bold"
              style={{ background: 'rgba(230,57,70,0.10)', color: '#E63946', border: '1px solid rgba(230,57,70,0.35)' }}>
              <span className="flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                ₱{owingTotal.toLocaleString()} still to collect from {owing.length} team{owing.length === 1 ? '' : 's'}
              </span>
              <span className="uppercase text-xs" style={{ fontFamily: 'Oswald, sans-serif' }}>Open Payments →</span>
            </button>
          );
        })()}

        {/* Tabs — shared EventTabs (same system as the open play page) */}
        <EventTabs
          testPrefix="tab"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'dashboard', label: 'Dashboard', icon: BarChart3,
              caption: 'The overview: fees collected, registrations, match progress. View-only — nothing here changes data.' },
            { key: 'players', label: 'Payments', icon: Wallet,
              hidden: t.event_style === 'announce' && t.announce_registration !== 'onsite',
              caption: 'The money desk: collect entry fees, review GCash proofs, and see who still owes and who collected what.' },
            { key: 'teams', label: 'Teams', icon: Users,
              hidden: t.event_style === 'announce' && t.announce_registration !== 'onsite',
              caption: 'Registration desk: add teams manually, share the sign-up link, and review payment proofs.' },
            { key: 'invitations', label: 'Invitations', icon: Mail,
              hidden: t.event_style === 'announce',
              caption: 'Invite teams by email and track their RSVPs.',
              badge: invitations.filter(i => i.status === 'pending').length },
            { key: 'bracket', label: t.format === 'round_robin' ? 'Matches' : 'Bracket', icon: GitBranch,
              hidden: t.event_style === 'announce',
              caption: 'Run the games: brackets, live scores, court assignments — completed results can be corrected here.' },
            { key: 'standings', label: 'Standings', icon: Trophy, hidden: t.event_style === 'announce' || t.format !== 'round_robin',
              caption: 'Round-robin table — wins, points and rankings.' },
            { key: 'awards', label: 'Awards', icon: Trophy, hidden: t.event_style !== 'announce',
              caption: 'Prizes shown on your public page — and where you declare the winners once the tournament finishes.' },
            { key: 'qrcodes', label: 'QR Codes', icon: QrCode,
              hidden: t.event_style === 'announce' && t.announce_registration === 'none',
              caption: 'Printable QR posters: spectators, registration, and court scorers.' },
          ]}
        />

        {/* Awards Tab — announce-style events */}
        {tab === 'awards' && <AwardsPanel t={t} onSaved={fetchAll} />}

        {/* Players/Payments Tab */}
        {tab === 'players' && (
          <PlayerManagementPanel
            mode="tournament"
            eventId={id}
            players={teams}
            entryFee={parseFloat(t.entry_fee || 0)}
            divisions={t.divisions || []}
            onSendLink={(tm) => setPaylinkTeam(tm)}
            onRefresh={fetchAll}
          />
        )}

        {/* Teams Tab */}
        {tab === 'teams' && (
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-4">
              {!t.bracket_generated && <AddTeamForm tournament={t} slug={slug} onAdded={fetchAll} />}
              {slug && (
                <div className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                  <p className="text-xs uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Public Registration</p>
                  <div className="flex flex-col items-center p-3 rounded-lg mb-3" style={{ background: '#fff' }}>
                    <QRCodeSVG value={regUrl} size={150} level="H" includeMargin />
                  </div>
                  <button data-testid="copy-reg-link" onClick={copyReg} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                    <Copy className="h-3.5 w-3.5" /> Copy Registration Link
                  </button>
                  <p className="text-xs mt-2 text-center" style={{ color: '#525252' }}>Players scan to self-register {t.status !== 'registration_open' ? '(open registration first)' : ''}</p>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Registered Teams ({teams.length})</p>
              <div className="space-y-4">
                {teams.length === 0 && <p className="text-sm" style={{ color: '#525252' }}>No teams yet.</p>}
                {groupByDivision(teams, t.divisions).map(g => (
                  <div key={g.divId || 'all'}>
                    {g.divName && (
                      <p className="text-xs uppercase font-bold mb-2 px-1" style={{ color: '#E63946' }}>{g.divName} <span style={{ color: '#525252' }}>({g.matches.length})</span></p>
                    )}
                    <div className="space-y-2">
                      {g.matches.map(tm => (
                  <div key={tm.id} data-testid={`team-row-${tm.id}`} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <TeamLogo logo={logoMap[tm.id]} name={tm.team_name} size={36} onClick={openLightbox} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold" style={{ color: tm.status === 'eliminated' ? '#737373' : '#fff', textDecoration: tm.status === 'eliminated' ? 'line-through' : 'none' }}>{tm.team_name}</p>
                        <p className="text-xs truncate" style={{ color: '#737373' }}>
                          {tm.players?.map(p => p.name).join(' / ')}
                          {tm.registered_by === 'public' ? ' · self-registered' : tm.registered_by === 'invitation' ? ' · invited' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {tm.pay_amount_choice === 'deposit50' && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>50% deposit</span>
                      )}
                      {tm.payment_status && tm.payment_status !== 'free' && (
                        tm.payment_status === 'pending' && tm.proof_url ? (
                          <button data-testid={`proof-view-${tm.id}`} onClick={() => openProofViewer(teams, tm)}
                            className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full transition-all"
                            style={{ background: '#F59E0B22', color: '#F59E0B', border: '1px solid #F59E0B44', cursor: 'pointer' }}>
                            <Eye className="h-3 w-3" /> Proof sent
                          </button>
                        ) : (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                            background: tm.payment_status === 'paid' ? '#22C55E22' : tm.payment_status === 'pending' ? '#F59E0B22' : '#73737322',
                            color: tm.payment_status === 'paid' ? '#22C55E' : tm.payment_status === 'pending' ? '#F59E0B' : '#A3A3A3',
                          }}>
                            {tm.payment_status === 'paid' ? 'Paid' : tm.payment_status === 'pending' ? 'Proof sent' : 'Pay at venue'}
                          </span>
                        )
                      )}
                      {tm.payment_status !== 'free' && balanceOf(tm) > 0 && (
                        <button data-testid={`collect-${tm.id}`} onClick={() => openCollect(tm)}
                          className="text-xs px-2 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid #22C55E44' }}
                          title="Collect payment — opens the review screen">
                          Collect ₱{balanceOf(tm).toLocaleString()}
                        </button>
                      )}
                      {!t.bracket_generated && (
                        <button onClick={() => removeTeam(tm)} style={{ color: '#737373' }}><X className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Invitations Tab */}
        {tab === 'invitations' && (
          <InvitationsPanel tournamentId={id} invitations={invitations} bracketGenerated={t.bracket_generated} divisions={t.divisions} onRefresh={fetchAll} />
        )}

        {/* Tournament Dashboard Tab */}
        {tab === 'dashboard' && (
          <>
            <OrganizerAccessCard apiBase={`/sports/tournaments/${id}`} />
            <TournamentDashboard tournamentId={id} tournament={t} />
          </>
        )}

        {/* QR Codes Hub Tab */}
        {tab === 'qrcodes' && (
          <QRCodesHub tournament={t} matches={matches} slug={slug} tournamentId={id} />
        )}

        {/* Bracket / Matches Tab */}
        {tab === 'bracket' && (
          <div>
            {matches.length === 0 ? (
              <div className="rounded-2xl p-10 text-center" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
                <ListChecks className="h-8 w-8 mx-auto mb-2" style={{ color: '#2A2A2A' }} />
                <p className="text-sm" style={{ color: '#A3A3A3' }}>Add at least 2 teams and generate the {t.pool_play ? 'pools' : 'bracket'}.</p>
              </div>
            ) : t.stage === 'pools' ? (
              <PoolStageView poolMatches={poolMatches} teams={teams} advancePerPool={t.advance_per_pool || 2}
                logoMap={logoMap} onMatch={(m) => window.open(`/sports/score/${m.qr_token}`, '_blank')} onLogo={openLightbox} onShowQR={setQrMatch} onReopen={requestReopen} />
            ) : (() => {
              const openScorer = (m) => window.open(`/sports/score/${m.qr_token}`, '_blank');
              const groups = groupByDivision(bracketMatches, t.divisions);
              const showList = bracketView === 'list' || t.format === 'round_robin';
              return (
                <>
                  {t.format !== 'round_robin' && (
                    <div className="flex gap-1 mb-4 p-1 rounded-lg" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', width: 'fit-content' }}>
                      {[['bracket', 'Bracket', GitBranch], ['list', 'List', ListChecks]].map(([k, l, Ic]) => (
                        <button key={k} data-testid={`bview-${k}`} onClick={() => setBracketView(k)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
                          style={{ background: bracketView === k ? '#E63946' : 'transparent', color: bracketView === k ? '#fff' : '#737373' }}>
                          <Ic className="h-3.5 w-3.5" /> {l}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="space-y-8">
                    {groups.map(g => (
                      <div key={g.divId || 'all'} data-testid={`division-bracket-${g.divId || 'all'}`}>
                        {g.divName && (
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-bold uppercase px-3 py-1 rounded-lg" style={{ background: '#E6394618', color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>{g.divName}</span>
                            <span className="text-xs" style={{ color: '#525252' }}>{g.matches.length} matches</span>
                          </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                          <div className="lg:col-span-2 min-w-0">
                            {showList ? (
                              <MatchListView matches={g.matches} onMatch={openScorer} logoMap={logoMap} onLogo={openLightbox} onShowQR={setQrMatch} onReopen={requestReopen} />
                            ) : (
                              <SingleDivisionBracket format={t.format} matches={g.matches} slug={slug} logoMap={logoMap} onLogo={openLightbox} onMatch={openScorer} onReopen={requestReopen} onShowQR={setQrMatch} />
                            )}
                          </div>
                          <div className="lg:col-span-1">
                            <RankingPanel format={t.format} matches={g.matches} standings={standings[g.divId] || standings[g.divId ?? 'null']} logoMap={logoMap} onLogo={openLightbox} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Standings Tab */}
        {tab === 'standings' && (
          <div className="space-y-6">
            {Object.keys(standings).length === 0 && <p className="text-sm" style={{ color: '#525252' }}>No standings yet.</p>}
            {Object.entries(standings).map(([divId, rows]) => (
              <div key={divId} className="rounded-xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                {divId !== 'null' && t.divisions?.find(d => d.id === divId) && (
                  <p className="px-4 py-2 text-xs uppercase font-bold" style={{ color: '#E63946', borderBottom: '1px solid #2A2A2A' }}>{t.divisions.find(d => d.id === divId).name}</p>
                )}
                <StandingsTable rows={rows} matches={matches} logoMap={logoMap} onLogo={openLightbox} />
              </div>
            ))}
          </div>
        )}
      </div>
      <LogoLightbox url={lightboxUrl} onClose={closeLightbox} />
      <PaymentLinkModal
        open={!!paylinkTeam}
        onClose={() => setPaylinkTeam(null)}
        title={paylinkTeam ? `Send Link — ${paylinkTeam.team_name}` : ''}
        subtitle={paylinkTeam ? `${t?.name || 'Tournament'} · ${paylinkTeam.division_name || paylinkTeam.division || ''}` : ''}
        baseAmount={paylinkTeam?.amount_due || t?.entry_fee || 0}
        onGenerate={async ({ amount_choice, amount_php }) => {
          const r = await sportsApi.post(
            `/sports/tournaments/${id}/teams/${paylinkTeam.id}/payment-link`,
            { amount_choice, amount_php }
          );
          fetchAll();
          return r.data;
        }} />
      {proofViewer && (
        <ProofViewerModal
          proofTeams={proofViewer.teams}
          initialIndex={proofViewer.index}
          onClose={() => { setProofViewer(null); fetchAll(); }}
          onCollect={(tm) => openCollect(tm, { amount: balanceOf(tm), method: 'gcash' })}
        />
      )}
      {reconcile && (
        <PaymentReconcileModal
          mode="tournament"
          eventId={id}
          players={reconcile.players}
          playerIndex={reconcile.index}
          initial={reconcile.initial}
          onSendLink={(tm) => setPaylinkTeam(tm)}
          onClose={() => setReconcile(null)}
          onSaved={async () => { fetchAll(); }}
        />
      )}
      <ScorerQRModal match={qrMatch} onClose={() => setQrMatch(null)} onCalled={fetchAll} />

      {/* Manual Seed Modal */}
      {seedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setSeedModal(false)} data-testid="seed-modal">
          <div className="rounded-2xl p-5 w-full max-w-lg" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
                Manual Seeding
              </p>
              <button onClick={() => setSeedModal(false)} data-testid="seed-modal-close" style={{ color: '#737373' }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs mb-3" style={{ color: '#A3A3A3' }}>
              Assign seed numbers (1 = top seed) to control first-round matchups. Seed 1 will meet the lowest seed, seed 2 the second-lowest, and so on.
              Leave blank for unseeded teams — they&apos;ll be placed after seeded ones in registration order.
            </p>
            <div className="space-y-2 mb-3">
              {teams.map((team, idx) => (
                <div key={team.id} data-testid={`seed-row-${team.id}`}
                  className="flex items-center gap-2 p-2 rounded-lg" style={{ background: '#141414', border: '1px solid #262626' }}>
                  <span className="text-xs tabular-nums" style={{ color: '#525252', width: 20 }}>{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: '#fff' }}>{team.team_name}</p>
                    {team.division_id && t.divisions?.find(d => d.id === team.division_id) && (
                      <p className="text-[10px]" style={{ color: '#666' }}>
                        {t.divisions.find(d => d.id === team.division_id).name}
                      </p>
                    )}
                  </div>
                  <input data-testid={`seed-input-${team.id}`} type="number" min="1"
                    value={manualSeeds[team.id] ?? ''}
                    onChange={e => setManualSeeds(s => ({ ...s, [team.id]: e.target.value }))}
                    placeholder="—"
                    style={{ width: 64, textAlign: 'center', padding: '6px 8px', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 6, fontSize: 14 }} />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid="seed-clear" onClick={() => setManualSeeds({})}
                className="flex-1 py-2 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#141414', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                Clear All
              </button>
              <button data-testid="seed-save" onClick={() => setSeedModal(false)}
                className="flex-1 py-2 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#E63946', color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {pinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setPinModal(null)} data-testid="pin-modal">
          <div className="rounded-2xl p-5 w-full" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <p className="text-sm font-bold mb-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
              {pinModal.mode === 'cancel' ? 'Cancel & Regenerate Bracket' : pinModal.mode === 'advance' ? 'Advance to Knockout' : pinModal.mode === 'reopen' ? `Correct Result · Match ${pinModal.matchNo}` : 'Enter Tournament PIN'}
            </p>
            {pinModal.mode === 'cancel' ? (
              <p className="text-xs mb-3" style={{ color: '#A3A3A3' }}>
                This deletes the current bracket and reopens registration so new teams can join.
                Only possible because no scores have been recorded yet.
                {!t.scorer_pin && ' This tournament has no PIN yet — the PIN you enter will be saved as its PIN.'}
              </p>
            ) : pinModal.mode === 'advance' ? (
              <p className="text-xs mb-3" style={{ color: '#A3A3A3' }}>Seeds the knockout bracket from the final pool standings (top finishers advance, pool winners kept apart). Enter the PIN to confirm.</p>
            ) : pinModal.mode === 'reopen' ? (
              <p className="text-xs mb-3" style={{ color: '#A3A3A3' }}>Reopens this finished match so you can re-score it. The winner is removed from the next round (blocked if that match has already started). Enter the PIN to confirm.</p>
            ) : (
              <p className="text-xs mb-3" style={{ color: '#A3A3A3' }}>This tournament is protected by a PIN. Enter it to generate.</p>
            )}
            <input data-testid="pin-modal-input" type="password" inputMode="numeric" autoFocus value={pinModal.pin}
              onChange={e => setPinModal(pm => ({ ...pm, pin: e.target.value, error: '' }))}
              onKeyDown={e => e.key === 'Enter' && (pinModal.mode === 'cancel' ? doCancel(pinModal.pin) : pinModal.mode === 'advance' ? doAdvance(pinModal.pin) : pinModal.mode === 'reopen' ? doReopen(pinModal.pin, pinModal.matchId) : doGenerate(pinModal.pin))}
              placeholder="Tournament PIN" style={{ ...inputStyle, textAlign: 'center', letterSpacing: 4, fontSize: 18 }} />
            {pinModal.error && <p data-testid="pin-modal-error" className="text-xs mt-2" style={{ color: '#E63946' }}>{pinModal.error}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPinModal(null)} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>Back</button>
              <button data-testid="pin-modal-confirm" disabled={genLoading || !pinModal.pin.trim()}
                onClick={() => pinModal.mode === 'cancel' ? doCancel(pinModal.pin) : pinModal.mode === 'advance' ? doAdvance(pinModal.pin) : pinModal.mode === 'reopen' ? doReopen(pinModal.pin, pinModal.matchId) : doGenerate(pinModal.pin)}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold uppercase"
                style={{ background: pinModal.mode === 'cancel' ? '#F59E0B' : pinModal.mode === 'advance' ? '#22C55E' : pinModal.mode === 'reopen' ? '#F59E0B' : '#E63946', color: pinModal.mode === 'generate' ? '#fff' : '#0A0A0A', opacity: (genLoading || !pinModal.pin.trim()) ? 0.5 : 1 }}>
                {genLoading ? 'Working…' : (pinModal.mode === 'cancel' ? 'Regenerate' : pinModal.mode === 'advance' ? 'Advance' : pinModal.mode === 'reopen' ? 'Reopen' : 'Generate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
